import React, { useState, useEffect } from 'react';
import { Title, Loading, useLogout } from 'react-admin';
import {
  API_URL, authHeaders,
  Card, StackedBarChart, StepTable, ErrorsList, Section, DaysSelector,
  STACK_COLORS, STACK_LABELS, STEP_PALETTE,
} from './ProcessingStatsShared';

const BidderProcessingStats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const logout = useLogout();

  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (!isLocal) return <div style={{ padding: 48, textAlign: 'center', color: '#888' }}>Brak dostępu.</div>;

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/tenders/processing-stats/?days=${days}`, { headers: authHeaders() })
      .then((r) => {
        if (r.status === 401) { logout(); return null; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => { if (d) { setData(d); setError(null); } })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <Loading />;
  if (error) return <div style={{ padding: 24, color: '#c62828' }}>Błąd: {error}</div>;
  if (!data) return null;

  const { summary, daily, bidder_steps, bidder_errors, bidder_step_errors, bidder_step_fixed, recent_resets } = data;

  const chartData = daily.map((d) => ({
    date: d.date,
    done: d.bidder_done || 0,
    failed: d.bidder_failed || 0,
    waiting: d.bidder_waiting || 0,
    running: d.bidder_running || 0,
    resets: d.bidder_resets || 0,
    fixed: d.bidder_fixed || 0,
  }));

  const donePct = summary.bidder_total
    ? Math.round(summary.bidder_done / summary.bidder_total * 100)
    : 0;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
      <Title title="Monitoring oferentów" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.35em', color: '#333' }}>Monitoring oferentów</h2>
          <p style={{ color: '#888', margin: '4px 0 0', fontSize: '0.83em' }}>Dane operacyjne — widoczne tylko lokalnie</p>
        </div>
        <DaysSelector days={days} setDays={setDays} />
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
        <Card label="Oferenci łącznie" value={summary.bidder_total} />
        <Card label="Ukończeni" value={summary.bidder_done} bg="#e8f5e9" color="#2e7d32" sub={`${donePct}%`} />
        <Card
          label="Błędy"
          value={summary.bidder_failed}
          bg={summary.bidder_failed > 0 ? '#ffebee' : undefined}
          color={summary.bidder_failed > 0 ? '#c62828' : undefined}
        />
        <Card label="Oczekujący" value={summary.bidder_waiting} bg="#fff8e1" color="#f57f17" />
        <Card label="Zresetowane ręcznie" value={summary.bidder_total_resets ?? 0} bg="#e8eaf6" color="#3949ab" />
        <Card
          label="Naprawione (po resecie)"
          value={summary.bidder_fixed ?? 0}
          bg="#e0f2f1" color="#00695c"
          sub={summary.bidder_total_resets ? `${Math.round((summary.bidder_fixed ?? 0) / summary.bidder_total_resets * 100)}%` : undefined}
        />
      </div>

      {/* Daily chart */}
      <Section title="Aktywność dzienna — Oferenci">
        <StackedBarChart
          data={chartData}
          keys={['done', 'failed', 'running', 'resets', 'fixed']}
          colors={[STACK_COLORS[0], STACK_COLORS[1], STACK_COLORS[3], '#7986cb', '#26a69a']}
          labels={[STACK_LABELS[0], STACK_LABELS[1], STACK_LABELS[3], 'Resety', 'Naprawione']}
        />
      </Section>

      {/* Errors per step — daily stacked bar chart */}
      <Section title="Błędy dziennie — per krok workflow" action={<span style={{ fontSize: '0.78em', color: '#888' }}>Kliknij legendę, aby ukryć/pokazać krok</span>}>
        <StackedBarChart
          data={(bidder_step_errors?.dates || []).map((d) => ({
            date: d,
            ...(bidder_step_errors?.blocks || []).reduce((acc, b) => {
              acc[b] = bidder_step_errors.data[d]?.[b] || 0;
              return acc;
            }, {}),
          }))}
          keys={bidder_step_errors?.blocks || []}
          colors={(bidder_step_errors?.blocks || []).map((_, i) => STEP_PALETTE[i % STEP_PALETTE.length])}
          labels={bidder_step_errors?.blocks || []}
        />
      </Section>

      {/* Fixed per step — daily stacked bar chart */}
      <Section title="Naprawione dziennie — per krok workflow" action={<span style={{ fontSize: '0.78em', color: '#888' }}>Kliknij legendę, aby ukryć/pokazać krok</span>}>
        <StackedBarChart
          data={(bidder_step_fixed?.dates || []).map((d) => ({
            date: d,
            ...(bidder_step_fixed?.blocks || []).reduce((acc, b) => {
              acc[b] = bidder_step_fixed.data[d]?.[b] || 0;
              return acc;
            }, {}),
          }))}
          keys={bidder_step_fixed?.blocks || []}
          colors={(bidder_step_fixed?.blocks || []).map((_, i) => STEP_PALETTE[i % STEP_PALETTE.length])}
          labels={bidder_step_fixed?.blocks || []}
        />
      </Section>

      {/* Recent resets table */}
      <Section
        title={<>Historia ręcznych napraw {recent_resets?.length > 0 && <span style={{ marginLeft: 6, background: '#e8eaf6', color: '#3949ab', borderRadius: 10, padding: '1px 8px', fontSize: '0.85em' }}>{recent_resets.length}</span>}</>}
      >
        {!recent_resets?.length
          ? <div style={{ color: '#bbb', fontSize: '0.85em' }}>Brak restartów w wybranym zakresie</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8em' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee' }}>
                  <th style={{ textAlign: 'left', padding: '5px 8px', color: '#888', fontWeight: 600 }}>Kiedy</th>
                  <th style={{ textAlign: 'left', padding: '5px 8px', color: '#888', fontWeight: 600 }}>Kto</th>
                  <th style={{ textAlign: 'left', padding: '5px 8px', color: '#888', fontWeight: 600 }}>Krok</th>
                  <th style={{ textAlign: 'left', padding: '5px 8px', color: '#888', fontWeight: 600 }}>Oferent / Przetarg</th>
                  <th style={{ textAlign: 'left', padding: '5px 8px', color: '#888', fontWeight: 600 }}>Poprzedni błąd</th>
                </tr>
              </thead>
              <tbody>
                {recent_resets.map((r) => {
                  const user = r['reset_by__first_name']
                    ? `${r['reset_by__first_name']} ${r['reset_by__last_name']}`.trim()
                    : r['reset_by__email'] || '—';
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: '#888' }}>
                        {new Date(r.reset_at).toLocaleString('pl-PL')}
                      </td>
                      <td style={{ padding: '6px 8px', fontWeight: 600, color: '#3949ab', whiteSpace: 'nowrap' }}>{user}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{ background: '#e8eaf6', color: '#3949ab', borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap' }}>
                          {r.reset_from_block}
                        </span>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <a href={`#/tenders/${r['workflow__bidder_offer__tender_id']}/show`} style={{ color: '#c62828', fontWeight: 600, textDecoration: 'none' }}>
                          {r['workflow__bidder_offer__tender__title']?.slice(0, 35) || '—'}{r['workflow__bidder_offer__tender__title']?.length > 35 ? '…' : ''}
                        </a>
                        <div style={{ color: '#888', fontSize: '0.9em' }}>{r['workflow__bidder_offer__bidder__name']?.slice(0, 35) || ''}</div>
                      </td>
                      <td style={{ padding: '6px 8px', color: '#777', fontStyle: 'italic', maxWidth: 260, wordBreak: 'break-word' }}>
                        {r.previous_error?.slice(0, 120) || <span style={{ color: '#bbb' }}>brak</span>}
                        {r.previous_error?.length > 120 ? '…' : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        }
      </Section>

      {/* Step distribution */}
      <Section title="Rozkład kroków workflow">
        <StepTable steps={bidder_steps} />
      </Section>

      {/* Errors */}
      <Section
        title={
          <>
            Ostatnie błędy
            {bidder_errors.length > 0 && (
              <span style={{ marginLeft: 6, background: '#ffebee', color: '#c62828', borderRadius: 10, padding: '1px 8px', fontSize: '0.85em' }}>
                {bidder_errors.length}
              </span>
            )}
          </>
        }
      >
        <ErrorsList errors={bidder_errors} type="bidder" />
      </Section>
    </div>
  );
};

export default BidderProcessingStats;
